import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

function getStringErrorMessage(value: unknown): string | null {
	if (typeof value !== "string") {
		return null;
	}

	const message = value.trim();
	if (!message) {
		return null;
	}

	if (!message.startsWith("{") && !message.startsWith("[")) {
		return message;
	}

	try {
		return getErrorMessage(JSON.parse(message));
	} catch {
		return message;
	}
}

function getErrorCandidates(error: unknown): Record<string, unknown>[] {
	if (!isRecord(error)) {
		return [];
	}

	return [
		isRecord(error.response) ? error.response.data : undefined,
		error.data,
		error.error,
		error,
		error.response,
	].filter((candidate): candidate is Record<string, unknown> =>
		isRecord(candidate),
	);
}

function normalizeErrorCode(code: string): string {
	return code
		.trim()
		.replace(/[^a-zA-Z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "")
		.toUpperCase();
}

export function getErrorCode(error: unknown): string | null {
	for (const candidate of getErrorCandidates(error)) {
		if (typeof candidate.code === "string" && candidate.code.trim()) {
			return normalizeErrorCode(candidate.code);
		}

		if (typeof candidate.error === "string" && candidate.error.trim()) {
			return normalizeErrorCode(candidate.error);
		}

		if (
			typeof candidate.message === "string" &&
			candidate.message.trim() &&
			/[a-zA-Z0-9]/.test(candidate.message)
		) {
			return normalizeErrorCode(candidate.message);
		}
	}

	return null;
}

export function getErrorStatus(error: unknown): number | null {
	for (const candidate of getErrorCandidates(error)) {
		if (typeof candidate.status === "number") {
			return candidate.status;
		}

		if (typeof candidate.statusCode === "number") {
			return candidate.statusCode;
		}
	}

	return null;
}

export function hasApiErrorCode(error: unknown, ...codes: string[]): boolean {
	const errorCode = getErrorCode(error);
	if (!errorCode) {
		return false;
	}

	return codes.some((code) => normalizeErrorCode(code) === errorCode);
}

export function isChatNotFoundError(error: unknown): boolean {
	if (
		hasApiErrorCode(
			error,
			"CHAT_NOT_FOUND",
			"CHAT_NOT_FOUND_OR_ALREADY_DELETED",
		)
	) {
		return true;
	}

	if (getErrorStatus(error) !== 404) {
		return false;
	}

	const message = getErrorMessage(error);
	return (
		message.includes("Chat not found") ||
		message.includes("Chat not found or already deleted") ||
		message.includes("聊天不存在") ||
		message.includes("聊天不存在或已删除")
	);
}

export function getErrorMessage(error: unknown): string {
	if (!error) {
		return "An unknown error occurred";
	}

	const stringMessage = getStringErrorMessage(error);
	if (stringMessage) {
		return stringMessage;
	}

	const candidates = getErrorCandidates(error);
	if (candidates.length === 0) {
		return "An unknown error occurred";
	}

	for (const candidate of candidates) {
		const candidateStringMessage =
			getStringErrorMessage(candidate.error) ??
			getStringErrorMessage(candidate.message);
		if (candidateStringMessage) {
			return candidateStringMessage;
		}

		// Handle Zod-OpenAPI default error format
		if (candidate.success === false && candidate.error) {
			if (
				isRecord(candidate.error) &&
				Array.isArray(candidate.error.issues) &&
				candidate.error.issues.length > 0
			) {
				const issue = candidate.error.issues[0];
				if (isRecord(issue)) {
					const issueMessage = getStringErrorMessage(issue.message);
					if (issueMessage) {
						return issueMessage;
					}
				}
			}
		}
	}

	// Handle standard Error object or { message: ... }
	const errorMessage = isRecord(error)
		? getStringErrorMessage(error.message)
		: null;
	if (errorMessage) {
		return errorMessage;
	}

	return "An unknown error occurred";
}
